import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Indonesian mirror of the corporate-documents-for-abroad guide. */
const TITLE = "Dokumen perusahaan Thailand untuk dipakai di luar negeri — cara pengesahan agar diterima";
const DESC =
  "Lapisan pengesahan yang dibutuhkan dokumen badan hukum Thailand, urutan langkah yang benar, dan titik periksa sebelum berkas diserahkan.";
const PATH = "/blog/company-documents-for-abroad";
const URL = `/id${PATH}`;
const PUBLISHED = "2026-08-11";

const FAQ = [
  {
    question: "Dokumen perusahaan apa yang biasanya perlu disahkan?",
    answer:
      "Umumnya sertifikat pendaftaran perusahaan, daftar pemegang saham, anggaran dasar, risalah rapat, surat kuasa, dan sertifikat pendaftaran PPN.",
  },
  {
    question: "Apakah perlu salinan terbitan baru dari kantor pendaftaran?",
    answer:
      "Sebaiknya ya. Banyak instansi penerima meminta salinan yang diterbitkan Department of Business Development dalam 1–3 bulan terakhir, lengkap dengan tanda tangan dan cap pejabat.",
  },
  {
    question: "Bagaimana urutan yang benar?",
    answer:
      "Ambil salinan baru, notarisasi dokumen yang dibuat perusahaan sendiri, pengesahan Departemen Konsuler, penerjemahan, lalu legalisasi kedutaan bila diminta.",
  },
  {
    question: "Apakah risalah rapat perlu dinotarisasi?",
    answer:
      "Risalah dan resolusi adalah dokumen internal perusahaan, sehingga tanda tangan penandatangan biasanya perlu disahkan advokat notarial sebelum pengesahan berikutnya.",
  },
  {
    question: "Diterjemahkan ke bahasa apa?",
    answer:
      "Sesuai permintaan instansi penerima, umumnya bahasa Inggris atau bahasa resmi negara tujuan. Beberapa negara mensyaratkan penerjemah yang diakui.",
  },
  {
    question: "Apakah harus menyerahkan dokumen asli?",
    answer:
      "Banyak instansi meminta dokumen asli yang telah disahkan lengkap. Siapkan satu set salinan tambahan untuk arsip dan langkah paralel.",
  },
  {
    question: "Apa yang paling sering memperlambat proses?",
    answer:
      "Salinan pendaftaran yang kedaluwarsa, dokumen internal tanpa notarisasi, nama perusahaan tidak konsisten antar terjemahan, dan stempel yang tidak diterjemahkan.",
  },
];

export const Route = createFileRoute("/id/blog/company-documents-for-abroad")({
  component: IdCorporateGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "id" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("company-documents-for-abroad", "id") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "dokumen perusahaan Thailand, legalisasi dokumen perusahaan, terjemahan dokumen perusahaan Thailand, pengesahan akta perusahaan",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "id"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Beranda", url: "/id" },
              { name: "Panduan", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("company-documents-for-abroad"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Corporate",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ d: string; note: string }> = [
  { d: "Sertifikat pendaftaran perusahaan", note: "Minta salinan terbitan baru dari Department of Business Development" },
  { d: "Daftar pemegang saham (บอจ.5)", note: "Banyak instansi meminta terbitan 1–3 bulan terakhir" },
  { d: "Anggaran dasar dan memorandum", note: "Dipakai untuk membuktikan kewenangan dan lingkup usaha" },
  { d: "Risalah rapat dan resolusi", note: "Dokumen internal — perlu notarisasi tanda tangan" },
  { d: "Surat kuasa untuk perwakilan", note: "Sebutkan kewenangan menandatangani, menerima, dan menyerahkan berkas" },
  { d: "Sertifikat PPN dan laporan keuangan", note: "Bila instansi penerima meminta bukti operasional" },
];

const ORDER: string[] = [
  "Minta daftar resmi dari instansi atau mitra di negara tujuan",
  "Ambil salinan terbitan baru dokumen pendaftaran perusahaan",
  "Notarisasi risalah, resolusi, dan surat kuasa",
  "Ajukan pengesahan ke Departemen Konsuler Kementerian Luar Negeri Thailand",
  "Terjemahkan lengkap termasuk stempel, segel, dan catatan kaki",
  "Legalisasi kedutaan negara tujuan bila diminta, lalu periksa nama dan tanggal",
];

const CHECKS: string[] = [
  "Nama perusahaan ditulis konsisten pada semua terjemahan",
  "Penandatangan benar-benar pihak berwenang menurut dokumen pendaftaran",
  "Usia salinan pendaftaran masih dalam batas yang diterima",
  "Terjemahan mencakup stempel dan baris catatan",
  "Jumlah set sesuai permintaan, ditambah satu set arsip internal",
  "Urutan pengesahan benar; jangan menerjemahkan sebelum pengesahan",
];

function IdCorporateGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/id" className="text-muted-foreground hover:text-foreground">
          Beranda
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Panduan
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="company-documents-for-abroad" locale="id" />
      <p className="answer-summary speakable-summary">
        Dokumen perusahaan Thailand memerlukan dua penanganan berbeda: dokumen yang diterbitkan kantor pendaftaran cukup
        diambil versi baru lalu disahkan, sedangkan risalah rapat dan surat kuasa yang dibuat perusahaan sendiri harus
        dinotarisasi lebih dulu. Setelah itu barulah Departemen Konsuler, penerjemahan, dan legalisasi kedutaan bila
        diminta.
      </p>

      <h2>1. Dokumen yang umum dipakai</h2>
      <ul>
        {DOCS.map((x) => (
          <li key={x.d}>
            <strong>{x.d}:</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>2. Urutan pengerjaan</h2>
      <ol>
        {ORDER.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Titik periksa sebelum menyerahkan</h2>
      <ul>
        {CHECKS.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. Pertanyaan yang sering diajukan</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. Sumber resmi dan layanan terkait</h2>
      <p className="text-sm">
        Referensi:{" "}
        <a href="https://www.dbd.go.th/" target="_blank" rel="noreferrer">
          Department of Business Development (Kementerian Perdagangan Thailand)
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Departemen Konsuler, Kementerian Luar Negeri Thailand
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/id/corporate" className="text-primary underline">
            Layanan dokumen perusahaan
          </Link>
        </li>
        <li>
          <Link to="/id/lgz" className="text-primary underline">
            Pengesahan dan legalisasi dokumen
          </Link>
        </li>
        <li>
          <Link to="/id/notary" className="text-primary underline">
            Notarisasi oleh advokat Notarial Services Attorney
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Konten ditinjau pada {PUBLISHED}. Persyaratan tiap instansi dan negara dapat berbeda, jadi konfirmasikan daftar
        resmi sebelum memulai. Bila Anda tidak ingin mengurus tiap langkah sendiri, tim kami dengan pengalaman lebih dari
        15 tahun dapat memberi konsultasi dan menangani seluruh proses — hubungi kami melalui telepon, LINE, atau email.
      </p>
    </article>
  );
}
